import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft, Radio, RefreshCw, Database, ShoppingCart } from "lucide-react";
import { api } from "@/lib/api";

export function OwnerSyncCard({ onSynced }) {
  const [health, setHealth] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const loadHealth = async () => {
    try {
      const { data } = await api.get("/admin/sync/health");
      setHealth(data);
    } catch (e) { console.warn("sync health load failed", e); }
  };

  const runReconcile = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync/reconcile");
      toast.success(`Owner sync done — ${data.products} updated, ${data.removed || 0} removed`);
      loadHealth();
      onSynced?.();
    } catch (e) {
      toast.error("Owner sync failed");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => { loadHealth(); }, []);

  return (
    <div data-testid="owner-sync-card" className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <ArrowRightLeft className="text-emerald-600" size={20} />
          <div>
            <h2 className="font-bold text-base text-slate-900">Owner App — Two-Way Live Sync</h2>
            <p className="text-xs text-slate-500">
              External Owner App database (<code className="bg-slate-100 px-1 rounded">{health?.owner_db || "rsm_central"}</code>) se real-time sync.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start">
          {health && (
            <span className={`text-xs font-bold px-3 py-1 rounded-full border flex items-center gap-1.5 ${health.owner_reachable ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
              <Radio size={12} className={health.owner_reachable ? "text-emerald-500 animate-pulse" : "text-rose-500"} />
              {health.owner_reachable ? "CONNECTED" : "OFFLINE"}
            </span>
          )}
          <button
            data-testid="owner-sync-now-btn"
            onClick={runReconcile}
            disabled={syncing}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg font-bold text-xs flex items-center gap-1.5"
          >
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync Now"}
          </button>
        </div>
      </div>

      {health && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <p className="text-[11px] font-semibold text-slate-500 uppercase">Owner Catalog</p>
              <p className="text-xl font-extrabold text-slate-900 mt-0.5" data-testid="owner-products-count">{health.owner_counts?.products ?? "—"}</p>
              <p className="text-[11px] text-slate-500">products · {health.owner_counts?.categories ?? "—"} categories</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <p className="text-[11px] font-semibold text-slate-500 uppercase">Synced Here</p>
              <p className="text-xl font-extrabold text-emerald-700 mt-0.5" data-testid="synced-products-count">{health.local_counts?.synced_products ?? "—"}</p>
              <p className="text-[11px] text-slate-500">of {health.local_counts?.products ?? "—"} total local products</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <p className="text-[11px] font-semibold text-slate-500 uppercase">Orders → Owner</p>
              <p className="text-xl font-extrabold text-slate-900 mt-0.5">{health.owner_counts?.orders ?? "—"}</p>
              <p className="text-[11px] text-slate-500">pushed to owner app</p>
            </div>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-2 text-xs text-slate-600">
            <div className="flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-100 p-2.5">
              <Database size={14} className="text-emerald-600 mt-0.5 shrink-0" />
              <span><strong>Owner → Web (real-time):</strong> Products, categories, price & stock owner app se turant yaha aate hain.</span>
            </div>
            <div className="flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-100 p-2.5">
              <ShoppingCart size={14} className="text-sky-600 mt-0.5 shrink-0" />
              <span><strong>Web → Owner (~8s):</strong> Orders, POS bills, khata & stock changes owner app me push hote hain.</span>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-3">
            Last pull: {health.last_pull_at ? new Date(health.last_pull_at).toLocaleString() : "—"} · Last push: {health.last_push_at ? new Date(health.last_push_at).toLocaleString() : "—"}
          </p>
        </>
      )}
    </div>
  );
}
